import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForceLanguage } from 'hooks/use-force-language';
import { DEFAULT_LANGUAGE } from 'i18n';
import { useScrollTopOnChange } from 'hooks/use-scroll-top-on-change';
import { LogoMark } from 'components/global/LogoMark';
import { AuthStepper } from './AuthStepper';

interface OnboardingWizardProps {
  /** Ordered step labels (Organization / Use case / Invite group). */
  steps: string[];
  /** 0-based index of the active step. */
  current: number;
  /** When present, renders a "Skip for now" action in the top bar. */
  onSkip?: () => void;
  children: React.ReactNode;
}

/** Rendered outside the Studio shell, so it applies its own surface and forces Uzbek like the auth pages. */
export function OnboardingWizard({ steps, current, onSkip, children }: OnboardingWizardProps) {
  useForceLanguage(DEFAULT_LANGUAGE);
  useScrollTopOnChange(current);
  const { t } = useTranslation();

  return (
    <div
      data-surface="studio"
      className="flex min-h-screen w-full flex-col bg-muted/30 px-4 py-6 text-foreground sm:py-10"
    >
      <div className="mx-auto w-full max-w-3xl">
        <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
          {/* Brand top bar */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center gap-2">
              <LogoMark size={26} />
              <span className="font-display text-[15px] font-extrabold tracking-[-0.02em]">Examing</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                {t('auth.step_x_of_n', { current: current + 1, total: steps.length })}
              </span>
              {onSkip ? (
                <button
                  type="button"
                  onClick={onSkip}
                  className="font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t('auth.skip_for_now')}
                </button>
              ) : null}
            </div>
          </div>

          {/* Stepper + step body */}
          <div className="px-6 py-8 sm:px-10">
            <AuthStepper steps={steps} current={current} className="mx-auto max-w-xl" />
            <div className="mt-8">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingWizard;
